/**
 * libs
 */
import { IClientSubscribeOptions, IClientSubscribeProperties } from "mqtt";

/**
 * models
 */
import { TopicHandler } from "../topic-management/topic-handler";
import { TopicRegistry } from "../topic-management/topic-registry";

/**
 * Manages a group of MQTT topics associated with a specific feature or profile.
 * @class ProfileHandler
 * @description Encapsulates business logic for a named profile (e.g., 'DeviceStatus', 'Notifications').
 * It interacts with the `TopicRegistry` to manage subscriptions specific to this profile.
 */
export class ProfileHandler {

    /**
     * Unique identifier for this profile instance (format: `connectionId:profileName`).
     * @public
     */
    public readonly profileId!: string 

    /**
     * Creates an instance of ProfileHandler.
     * @constructor
     * @param {string} name - The name of the profile.
     * @param {string} connectionId - The ID of the connection this profile belongs to.
     * @param {TopicRegistry} _topicRegistry - The registry to use for topic creation.
     * @param {IClientSubscribeOptions | IClientSubscribeProperties} options - Subscription options for this profile.
     * @param {string} [description] - Optional description of what this profile does.
     * @throws {Error} If arguments are invalid.
     */
    constructor(
        public readonly name: string,
        public readonly connectionId: string,
        private readonly _topicRegistry: TopicRegistry,
        public readonly options: IClientSubscribeOptions | IClientSubscribeProperties,
        public readonly description?: string
    ) {

        // global validation
        if (typeof this.name !== 'string' || this.name.length === 0) {
            throw new Error('Name must be a string & non-empty');
        }
        if (typeof this.connectionId !== 'string' || this.connectionId.length === 0) {
            throw new Error('Connection ID must be a string & non-empty');
        }
        if (!(this._topicRegistry instanceof TopicRegistry)) {
            throw new Error('Topic registry must be an instance of TopicRegistry');
        }
        if (typeof this.options !== 'object') {
            throw new Error('Options must be an object');
        }

        // Generate the profile ID
        this.profileId = this._generateProfileId(this.name, this.connectionId);

    }

    /**
     * Retrieves or creates a topic handler for this profile.
     * @public
     * @template TPayload - Expected payload type.
     * @param {string} topic - The topic string.
     * @returns {TopicHandler<TPayload>} The topic handler.
     */
    public topic<TPayload = any>(topic: string): TopicHandler<TPayload> {
        return this._topicRegistry.createOrGetTopic<TPayload>(topic);
    }

    /**
     * Manually adds an existing topic handler to this profile context.
     * @public
     * @param {string} topic - The topic string.
     * @param {TopicHandler} handler - The handler instance.
     */
    public addTopic(topic: string, handler: TopicHandler): void {
        this._topicRegistry.add(topic, handler);
    }

    /**
     * Removes a topic from this profile (and the underlying registry).
     * @public
     * @param {string} topic - The topic string to remove.
     */
    public deleteTopic(topic: string): void {
        this._topicRegistry.remove(topic);
    }

    /**
     * Returns an array of all active topics associated with the underlying registry.
     * Note: Since the registry is shared per connection, this might return all topics for that connection.
     * @public
     * @returns {TopicHandler[]} List of topic handlers.
     */
    public topics(): TopicHandler[] {
        return this._topicRegistry.topicsArray;
    }

    /**
     * Generates a unique ID for the profile.
     * @private
     */
    private _generateProfileId(
        name: string,
        connectionId: string,
    ): string {
        return `${connectionId}:${name}`;
    }

    /**
     * Returns a string representation of the profile.
     * @public
     */
    public toString(): string {
        
        const _defaultDescription = this.description?.toString() ?? 'No description provided';

        return `
            ProfileHandler(
                name: ${this.name}, 
                connectionId: ${this.connectionId},
                description: ${_defaultDescription}
            )
        `;
    }
    
}
